import { todayInTz } from '@/lib/facilityTimezone';
import type { PlanTaskRawPayload } from '@/services/tasks.service';

/**
 * Form state for the Task Edit Modal — full parity with legacy
 * plan-task-details2.php. Every field maps 1:1 to a plan_tasks column or to
 * a UI control whose value is folded into the persisted columns at save time
 * (see assembleSavePayload below).
 */
export interface TaskFormState {
  // Item / canned task (legacy lines 678-707)
  taskID: string;          // selected canned task ID; '' = none, 'custom' = custom name
  customTaskName: string;  // legacy customPlanTaskName

  // Patient/Plan (only used on NEW; on edit these are read from the row)
  patientID: string;
  planID: string;

  // Priority + completion (legacy lines 712-744)
  priority: string;        // '1' Low | '2' Medium | '3' High
  required: string;        // '0' = allow completion before due | '1' = required
  active: string;          // '0' | '1'

  // Dates (legacy lines 762-876)
  timeAmount: string;      // 0-364
  timeType: string;        // '1' days | '2' weeks | '3' months | '4' years
  beforeOrAfter: string;   // '' (after, default when timeAmount >= 0) | '-' (before)
  dateType: string;        // '1'-'9' (see DATE_TYPE_OPTIONS)
  customDate: string;      // YYYY-MM-DD; only meaningful when dateType='2'
  dateStarted: string;     // YYYY-MM-DD

  // Recurring (legacy lines 878-936)
  recurring: string;       // '0' Once | '1' Recurring
  recurringNumber: string; // 2-100
  recurringAmount: string; // 1-364
  recurringType: string;   // '1' days | '2' weeks | '3' months | '4' years

  // Notes (legacy lines 943-958)
  notes: string;

  // Assignment (legacy lines 1031-1100)
  assignedToID: string;    // 'coord' | 'nurse' | 'doc' | 'rec' | 'role_<X>' | 'group_<X>' | 'user' | 'ftp'
  assignedUserID: string;  // user ID when assignedToID='user'; serialized to sendToID
  // Create only. Several people picked by hand; the server writes one task per
  // entry, the same way a role's "(Individual Tasks)" option already does.
  // Editing an existing task still uses the single `assignedUserID` above.
  assignedUserIDs: string[];

  // Auto task type (legacy lines 982-1019)
  autoTaskID: string;      // '0' Human | '1' Email | '2' SMS | '3-5' Opt-out | '6' Tag | '7' Email Survey | '8' Stage | '9' SMS Survey

  // Conditional content fields (autoTaskID-driven; see selectAutoTaskType in tasks.js)
  // Email branch (autoTaskID=1)
  sendEmailToID: string;       // '1' Patient | '2' Coord | '3' Doc | '4' Nurse → sendToID (legacy 1290-1301)
  emailHeaderFooterID: string; // → headerFooterID (legacy 1305-1324)
  emailContentID: string;      // → contentID (legacy 1326-1345)
  // SMS branch (autoTaskID=2)
  sendSMSToID: string;         // '1' Patient → sendToID (legacy 1257)
  smsContentID: string;        // → contentID (legacy 1263-1282)
  // Tag branch (autoTaskID=6)
  tagCustomID: string;         // tags_custom.tagsCustomID → contentID (legacy 1137)
  // Survey branches (autoTaskID=7 or 9). Phase 0 decision: fix legacy bug; Survey selector visible.
  surveyID: string;            // surveys.id → headerFooterID (legacy 1187 quirk)
  surveyEmailContentID: string;// email content selected for Email Survey → contentID
  surveySmsContentID: string;  // sms content selected for SMS Survey → contentID

  // Documents (autoTaskID=1 only per Phase 0 decision; legacy bug #4)
  attachments: string;     // CSV: 'P_<packetID>' for packets, plain '<documentID>' for files

  // Tracking
  noteID: string;          // typically '0'
}

/**
 * autoTaskIDs whose `sendToID` holds a send-target enum rather than a user id
 * (Email, SMS, Email Survey Link, SMS Survey Link). Assigning several people is
 * unavailable for these because the one column cannot carry both; the server
 * enforces the same rule in AssignedUserListExpander.
 */
export const SEND_TO_ID_IS_RECIPIENT_TYPE = ['1', '2', '7', '9'];

/** Legacy date columns are Pacific wall clock; so is the backend's idea of today. */
const PACIFIC_TZ = 'America/Los_Angeles';

/**
 * Today's date as the server sees it. The backend computes `lastDue` and
 * `dateStarted` with ClockService::pacificToday(), and the Due Today bucket
 * compares against the same clock, so a create default anchored anywhere else
 * disagrees with the tab that decides where the task lands.
 *
 * `new Date().toISOString().slice(0, 10)` yields the UTC day, which from 5pm
 * Pacific onward is already tomorrow — a task assigned for today was created
 * with lastDue = tomorrow and showed up under Upcoming instead of Due Today.
 */
export function pacificTodayIso(): string {
  return todayInTz(PACIFIC_TZ);
}

/**
 * Blank form for a new task. A factory, not a module-level const: `pacificTodayIso()`
 * inside a const is evaluated once when the module is first loaded, so a tab
 * left open across midnight would prefill the previous day.
 */
export function makeEmptyForm(): TaskFormState {
  return {
    taskID: 'custom', customTaskName: '',
    patientID: '', planID: '',
    priority: '2', required: '0', active: '1',
    timeAmount: '0', timeType: '1', beforeOrAfter: '', dateType: '2', customDate: pacificTodayIso(),
    dateStarted: pacificTodayIso(),
    recurring: '0', recurringNumber: '2', recurringAmount: '1', recurringType: '1',
    notes: '',
    assignedToID: 'myself', assignedUserID: '', assignedUserIDs: [],
    autoTaskID: '0',
    sendEmailToID: '0', emailHeaderFooterID: '0', emailContentID: '0',
    sendSMSToID: '0', smsContentID: '0',
    tagCustomID: '0',
    surveyID: '0', surveyEmailContentID: '0', surveySmsContentID: '0',
    attachments: '',
    noteID: '0',
  };
}

/**
 * Assemble the wide PlanTaskRawPayload sent to the backend on save.
 * Folds conditional UI fields (sendEmailToID, surveyEmailContentID, etc.) into
 * the persisted plan_tasks columns (sendToID, headerFooterID, contentID) using
 * the legacy autoTaskID-driven rules from tasks.js saveTask 712-754. Signs the
 * timeAmount per legacy line 786 (negative when "before").
 *
 * Exported for tests: mounting the whole modal drags in a dozen services, and
 * the assignment folding below is the part worth pinning.
 */
export function assembleSavePayload(form: TaskFormState): PlanTaskRawPayload {
  // Sign the time amount: negative for "before"
  const numAmount = parseInt(form.timeAmount, 10) || 0;
  const signedTimeAmount = form.beforeOrAfter === '-' ? -numAmount : numAmount;

  // Derive the persisted task name from the Item picker selection
  // (legacy: $taskName from taskType vs customPlanTaskName, processor lines 169-176)
  const persistedTaskID = form.taskID === 'custom' || form.taskID === '' ? 0 : parseInt(form.taskID, 10);
  const persistedName = form.taskID === 'custom' || persistedTaskID === 0 ? form.customTaskName.trim() : '';

  // Conditional fields driven by autoTaskID (mirrors tasks.js saveTask 712-754)
  let sendToID = 0;
  let headerFooterID = 0;
  let contentID = 0;

  switch (form.autoTaskID) {
    case '1': // Email
      sendToID = parseInt(form.sendEmailToID, 10) || 0;
      headerFooterID = parseInt(form.emailHeaderFooterID, 10) || 0;
      contentID = parseInt(form.emailContentID, 10) || 0;
      break;
    case '2': // SMS
      sendToID = parseInt(form.sendSMSToID, 10) || 0;
      contentID = parseInt(form.smsContentID, 10) || 0;
      break;
    case '6': // Tag
      contentID = parseInt(form.tagCustomID, 10) || 0;
      break;
    case '7': // Email Survey Link
      // Phase 0 decision: fix legacy bug — Survey selector visible.
      // Survey ID stored in headerFooterID (legacy 1187 quirk).
      sendToID = 1;
      headerFooterID = parseInt(form.surveyID, 10) || 0;
      contentID = parseInt(form.surveyEmailContentID, 10) || 0;
      break;
    case '9': // SMS Survey Link
      sendToID = 1;
      headerFooterID = parseInt(form.surveyID, 10) || 0;
      contentID = parseInt(form.surveySmsContentID, 10) || 0;
      break;
    // 0, 3, 4, 5, 8: clear all three (mirrors legacy 749-754)
    default:
      sendToID = 0;
      headerFooterID = 0;
      contentID = 0;
  }

  // Group_X assignment: persist as the literal token (matches legacy line 1065 first form)
  // sendToID for assignedToID='user' comes from assignedUserID
  let finalAssignedToID = form.assignedToID;
  let finalSendToID = sendToID;
  if (form.assignedToID === 'user') {
    finalSendToID = parseInt(form.assignedUserID, 10) || 0;
  }
  // For autoTaskID-driven tasks, the auto-task's sendToID overrides the user selection
  // (legacy email/sms branches set sendToID to send-target enum, not user)
  if (['1', '2', '7', '9'].includes(form.autoTaskID)) {
    finalSendToID = sendToID;
  }

  // Several people picked by hand: the server expands this into one task each
  // (AssignedUserListExpander). sendToID still carries the first of them so the
  // request degrades to today's single-assignee create if the list is dropped.
  // Omitted for the autoTaskIDs above, where sendToID is a send-target enum the
  // server refuses to overwrite.
  const pickedUserIDs =
    finalAssignedToID === 'user' && !SEND_TO_ID_IS_RECIPIENT_TYPE.includes(form.autoTaskID)
      ? form.assignedUserIDs.map(id => parseInt(id, 10)).filter(id => id > 0)
      : [];
  if (pickedUserIDs.length > 0) {
    finalSendToID = pickedUserIDs[0];
  }

  // Phase 0 decision #7: attachments only sent for autoTaskID=1 (Email)
  const attachments = form.autoTaskID === '1' ? form.attachments : '';

  return {
    taskID: persistedTaskID > 0 ? persistedTaskID : 0,
    name: persistedName || '',
    priority: parseInt(form.priority, 10) || 2,
    required: parseInt(form.required, 10) || 0,
    active: parseInt(form.active, 10) || 0,

    timeType: parseInt(form.timeType, 10) || 1,
    timeAmount: signedTimeAmount,
    dateType: parseInt(form.dateType, 10) || 2,
    customDate: form.dateType === '2' ? (form.customDate || '') : '',
    dateStarted: form.dateStarted || '',

    recurring: parseInt(form.recurring, 10) || 0,
    recurringNumber: form.recurring === '1' ? parseInt(form.recurringNumber, 10) || 0 : 0,
    recurringAmount: form.recurring === '1' ? parseInt(form.recurringAmount, 10) || 0 : 0,
    recurringType: form.recurring === '1' ? parseInt(form.recurringType, 10) || 0 : 0,

    notes: form.notes || '',

    assignedToID: finalAssignedToID || '',
    sendToID: finalSendToID || 0,
    ...(pickedUserIDs.length > 0 ? { assignedUserIDs: pickedUserIDs } : {}),

    autoTaskID: parseInt(form.autoTaskID, 10) || 0,
    headerFooterID: headerFooterID || 0,
    contentID: contentID || 0,
    attachments: attachments || '',
    noteID: parseInt(form.noteID, 10) || 0,
  };
}
